import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Context for providing a portal container element.
 * This allows autocomplete dropdowns to render inside a Radix Dialog
 * instead of at document.body, avoiding the "inert" attribute that
 * Radix Dialog applies to siblings of its portal.
 */
const PortalContainerContext = createContext(null);

/**
 * Hook to get the portal container element.
 * Returns the container element if inside a PortalContainerProvider,
 * otherwise returns document.body.
 */
export function usePortalContainer() {
  const container = useContext(PortalContainerContext);
  // Return the container from context, or fall back to document.body
  if (container) {
    return container;
  }
  return typeof document !== 'undefined' ? document.body : null;
}

/**
 * Provider component that creates a portal container.
 * Wrap your content with this inside a Radix Dialog to allow
 * autocomplete dropdowns to render correctly.
 */
export function PortalContainerProvider({ children }) {
  const [containerElement, setContainerElement] = useState(null);

  // Callback ref to capture the DOM element
  const setContainerRef = useCallback((el) => {
    if (el) {
      setContainerElement(el);
    }
  }, []);

  return (
    <PortalContainerContext.Provider value={containerElement}>
      {children}
      {/* This div serves as the portal target for dropdowns */}
      <div
        ref={setContainerRef}
        style={{ position: 'relative', zIndex: 9999 }}
        data-portal-container="true"
      />
    </PortalContainerContext.Provider>
  );
}

export default PortalContainerContext;
